/**
 * Composer (06 §4.9) — emoji · attach · growing textarea · mic↔send swap.
 * Enter sends (desktop), Shift+Enter newline. Reply preview mounts above.
 * Draft persists per-chat. Voice-record mode toggles a recording UI.
 */
import { useEffect, useRef, useState } from 'react';
import { Smile, Paperclip, Mic, Send, Trash2, X } from 'lucide-react';
import type { Conversation } from '../../data/model';
import { useChatStore } from '../../stores/chatStore';
import { useUiStore } from '../../stores/uiStore';
import { colorForId } from '../../lib/format';
import { AttachmentSheet } from './AttachmentSheet';
import { EmojiPicker } from './EmojiPicker';
import styles from './Composer.module.css';

interface Props {
  conv: Conversation;
}

export function Composer({ conv }: Props) {
  const me = useChatStore((s) => s.me);
  const userById = useChatStore((s) => s.userById);
  const draft = useChatStore((s) => s.getConversation(conv.id)?.draft ?? '');
  const reply = useChatStore((s) => s.replyTo[conv.id] ?? null);
  const setDraft = useChatStore((s) => s.setDraft);
  const setReplyTo = useChatStore((s) => s.setReplyTo);
  const sendText = useChatStore((s) => s.sendText);
  const notifyTyping = useChatStore((s) => s.notifyTyping);
  const toast = useUiStore((s) => s.toast);

  const [showEmoji, setShowEmoji] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize 1→6 lines.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }, [draft]);

  // Recording timer.
  useEffect(() => {
    if (!recording) return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [recording]);

  const hasText = draft.trim().length > 0;

  const submit = () => {
    if (!hasText) return;
    sendText(conv.id, draft);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  if (recording) {
    return (
      <div className={styles.bar}>
        <button
          className={styles.iconBtn}
          onClick={() => { setRecording(false); setElapsed(0); toast('Recording cancelled'); }}
          aria-label="Cancel recording"
        >
          <Trash2 size={22} className={styles.recDanger} />
        </button>
        <div className={styles.recording}>
          <span className={styles.recDot} aria-hidden="true" />
          <span className={styles.recTime}>
            {String(Math.floor(elapsed / 60)).padStart(2, '0')}:
            {String(elapsed % 60).padStart(2, '0')}
          </span>
          <span className={styles.recHint}>Recording… release to send</span>
        </div>
        <button
          className={styles.send}
          onClick={() => { setRecording(false); setElapsed(0); toast('Voice messages are stubbed in the demo'); }}
          aria-label="Send voice message"
        >
          <Send size={20} />
        </button>
      </div>
    );
  }

  const replySender =
    reply && (reply.senderId === me.id ? 'You' : userById(reply.senderId)?.displayName ?? 'Unknown');

  return (
    <div className={styles.wrap}>
      {reply && replySender && (
        <div className={styles.replyBar}>
          <div className={styles.replyBody} style={{ borderInlineStartColor: colorForId(reply.senderId) }}>
            <span className={styles.replyName} style={{ color: colorForId(reply.senderId) }}>
              {replySender}
            </span>
            <span className={styles.replySnippet}>{reply.text || 'Media'}</span>
          </div>
          <button className={styles.iconBtn} onClick={() => setReplyTo(conv.id, null)} aria-label="Cancel reply">
            <X size={20} />
          </button>
        </div>
      )}

      {showEmoji && (
        <EmojiPicker
          onPick={(emoji) => {
            setDraft(conv.id, draft + emoji);
            textareaRef.current?.focus();
          }}
          onClose={() => setShowEmoji(false)}
        />
      )}

      <div className={styles.bar}>
        <button
          className={styles.iconBtn}
          onClick={() => setShowEmoji((o) => !o)}
          aria-label="Emoji"
          aria-pressed={showEmoji}
        >
          <Smile size={24} />
        </button>
        <button className={styles.iconBtn} onClick={() => setShowAttach(true)} aria-label="Attach">
          <Paperclip size={22} />
        </button>

        <textarea
          ref={textareaRef}
          className={styles.input}
          value={draft}
          rows={1}
          placeholder="Type a message"
          aria-label="Type a message"
          onChange={(e) => {
            setDraft(conv.id, e.target.value);
            notifyTyping(conv.id);
          }}
          onKeyDown={onKeyDown}
        />

        {hasText ? (
          <button className={styles.send} onClick={submit} aria-label="Send">
            <Send size={20} />
          </button>
        ) : (
          <button
            className={styles.send}
            onClick={() => setRecording(true)}
            aria-label="Record voice message"
          >
            <Mic size={20} />
          </button>
        )}
      </div>

      <AttachmentSheet
        open={showAttach}
        onClose={() => setShowAttach(false)}
        onPick={(kind) => {
          setShowAttach(false);
          toast(`${kind} picker stubbed in the demo`);
        }}
      />
    </div>
  );
}
